import CircleIcon from "@mui/icons-material/Circle";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import CustomTooltip from "../../../../../../../../components/CustomTooltip";
import Flex from "../../../../../../../../components/Flex";
import Text from "../../../../../../../../components/Text";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../../components/app/DeleteModal";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useArchiveCareplan } from "../../../../../../../../queries/care-plan/use-patient-careplan-archive";
import { useClinicalSummaryLink } from "../../../../../../../../queries/encounter/use-clinical-summary-link";
import { ClinicalSummaryLinkRequestClinicalSummaryTypeEnum } from "../../../../../../../../services";
import { formatDate } from "../../../../../../../../utils/date-time-utility";
import {
  DEFAULT_KEY_LABEL_COLOR,
  DEFAULT_VALUE_LABEL_LIST,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
  transformMultiSelectFrontEndVal,
} from "../../../../../../../../utils/utils";
import UpsertCarePlanDrawer from "../../care-plan/UpsertCarePlanDrawer";
import ActionableHoverNew from "../ActionableHoverNew";
import { RightAction } from "../PatientRecords";
import PastCarePlan from "./PastCarePlan";
import { CareOptions } from "./SummaryConstant";
import { CarePlanTooltip } from "./encounterTooltips/CarePlanTooltip";
import LoadingAndEmptyStateWrapper from "./commonWrappers/LoadingAndEmptyStateWrapper";
import { useRecordsScroll } from "./RecordsScrollContext";
interface props {
  carePlanList: any;
  loading: boolean;
}

const Care = ({ carePlanList, loading }: props) => {
  const carePlanDisclosure = useDisclosure();
  const deletePastCarePlanDisclosure = useDeleteDisClosure();
  const { mutateAsync: archiveCareplan } = useArchiveCareplan();

  const [disabledTooltips, setDisabledTooltips] = useState<{
    [key: number]: boolean;
  }>({});
  const { mutateAsync: linkClinicalDataToAppointment } =
    useClinicalSummaryLink();
  const queryClient = useQueryClient();
  const appointmentid = useQueryParams("appointmentid");
  const showSnackbar = useSnackbar();
  const { isScrolling } = useRecordsScroll();

  const clickHandler = (v: string, idx?: number) => {
    if (v === "Edit") {
      const carePlanTransformed = {
        ...carePlanList?.data?.content[idx ?? 0],
        icdCodes: transformMultiSelectFrontEndVal(
          DEFAULT_VALUE_LABEL_LIST,
          carePlanList?.data?.content[idx ?? 0]?.icdCodes
        ),
      };
      carePlanDisclosure.open(carePlanTransformed);
    }
    // note : we are just posting care plan to note, but we are not showing care plan in any of the visit notes.
    // need to ask wheter history care plan should be add /( hit post API ) in visit note or not.
    if (v === "ExportToNote") {
      linkClinicalDataToAppointment({
        clinicalSummaryLinkRequest: {
          clinicalSummaryType:
            ClinicalSummaryLinkRequestClinicalSummaryTypeEnum.Careplan,
          recordId: carePlanList?.data?.content[idx ?? 0]?.uuid,
          appointmentId: appointmentid ?? "",
        },
        xTENANTID: getXtenantId(),
      })
        .then((res) => {
          showSnackbar(getSuccessSnackbar(res));
          queryClient.invalidateQueries({
            queryKey: ["patient-careplan-list"],
          });
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
        });
    }
    if (v === "Delete") {
      deletePastCarePlanDisclosure.open(
        carePlanList?.data?.content[idx ?? 0]?.uuid
      );
    }
  };
  const handleActionAreaToggle = (idx: number, isHovered: boolean) => {
    setDisabledTooltips((prev) => ({ ...prev, [idx]: isHovered }));
  };

  const handleMenuToggle = (idx: number, isOpen: boolean) => {
    setDisabledTooltips((prev) => ({ ...prev, [idx]: isOpen }));
  };

  const [showCarePlanHistory, setShowCarePlanHistory] = useState(false);
  function archiveHandler<T>(value: boolean, id: T) {
    return archiveCareplan({
      carePlanId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId() ?? undefined,
    }).then((res) => {
      queryClient.invalidateQueries({
        queryKey: ["patient-careplan-list"],
      });
      return res;
    });
  }
  return (
    <>
      <DeleteModal
        w="380px"
        open={deletePastCarePlanDisclosure.opened}
        onClose={deletePastCarePlanDisclosure.close}
        id={deletePastCarePlanDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this Care Plan"}
      />
      <Flex flexDirection={"column"} width={"100%"} gap={1}>
        <UpsertCarePlanDrawer
          curFormState={"add"}
          disclosureHost={carePlanDisclosure}
        />
        <Flex alignItems={"center"} gap={1} ml={1}>
          <CircleIcon sx={{ fontSize: 10 }} />
          <Text fontWeight={600} color="text.secondary">
            Current Care Plan
          </Text>
        </Flex>
        <LoadingAndEmptyStateWrapper
          loading={loading}
          isEmpty={carePlanList?.data?.content?.length === 0}
          emptyMessage="No Current Care Plan found"
        />
        {carePlanList?.data?.content?.map((it: any, idx: number) => {
          const isTooltipDisabled = disabledTooltips[idx] || false;
          return (
            <Flex key={idx} width={"100%"}>
              <CustomTooltip
                key={it?.uuid}
                content={<CarePlanTooltip key={idx} data={it} />}
                position="right"
                disableTooltip={isTooltipDisabled || isScrolling}
              >
                <ActionableHoverNew
                  renderAction={
                    <div
                      onMouseEnter={() => handleActionAreaToggle(idx, true)}
                      onMouseLeave={() => handleActionAreaToggle(idx, false)}
                    >
                      <RightAction
                        handleClickOption={(v) => clickHandler(v, idx)}
                        menuList={CareOptions}
                        onMenuOpen={() => handleMenuToggle(idx, true)}
                        onMenuClose={() => handleMenuToggle(idx, false)}
                      />
                    </div>
                  }
                >
                  <CarePlanHistorysingleline it={it} idx={idx} />
                </ActionableHoverNew>
              </CustomTooltip>
            </Flex>
          );
        })}
        {showCarePlanHistory && <PastCarePlan />}

        <Flex justifyContent={"space-between"}>
          <Button
            endIcon={
              showCarePlanHistory ? (
                <ExpandLessOutlinedIcon />
              ) : (
                <ExpandMoreOutlinedIcon />
              )
            }
            onClick={() => {
              setShowCarePlanHistory(!showCarePlanHistory);
            }}
          >
            {showCarePlanHistory
              ? "Hide Care Plan History"
              : "Show Care Plan History"}
          </Button>
        </Flex>
      </Flex>
    </>
  );
};

export default Care;

export const CarePlanHistorysingleline = ({
  it,
  idx,
}: {
  it: any;
  idx: number;
}) => {
  return (
    <Flex flexDirection={"column"} width={"100%"} gap={2}>
      <Text isTabletext color={DEFAULT_KEY_LABEL_COLOR}>
        {idx + 1}. {it?.title} - {it?.condition} - {it?.deception} -{" "}
        {it?.endDate ? formatDate(it?.endDate) : ""}
      </Text>
    </Flex>
  );
};
